import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { getWarehouses, createWarehouse, setDefaultWarehouse, updateWarehouse, getAllInboundCapacitySummary } from '../lib/api';
import { useWarehouse } from '../context/WarehouseContext';
import { Buildings, Plus, Star, CheckCircle, Warning, Coins } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import WarehouseRateCardDialog from '../components/WarehouseRateCardDialog';

export default function Warehouses() {
  const { refreshWarehouses, switchWarehouse, currentWarehouseId } = useWarehouse();
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newWh, setNewWh] = useState({ name: '', code: '', address: '', contact_name: '', contact_email: '' });
  const [capacityByWh, setCapacityByWh] = useState({});   // wh_id → row
  const [rateCardFor, setRateCardFor] = useState(null);

  const fetchData = async () => {
    try {
      const [whs, cap] = await Promise.all([
        getWarehouses(),
        getAllInboundCapacitySummary(30).catch(() => ({ data: { warehouses: [] } })),
      ]);
      setWarehouses(whs.data);
      const map = {};
      (cap.data?.warehouses || []).forEach((row) => { map[row.warehouse_id] = row; });
      setCapacityByWh(map);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createWarehouse(newWh);
      setShowCreate(false);
      setNewWh({ name: '', code: '', address: '', contact_name: '', contact_email: '' });
      fetchData();
      refreshWarehouses();
    } catch (e) {
      console.error(e);
    }
  };

  const handleSetDefault = async (id) => {
    await setDefaultWarehouse(id);
    fetchData();
    refreshWarehouses();
  };

  const handleToggleActive = async (wh) => {
    await updateWarehouse(wh.id, { is_active: !wh.is_active });
    fetchData();
    refreshWarehouses();
  };

  return (
    <Layout>
      <div data-testid="warehouses-page" className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Warehouses</h1>
            <p className="text-muted-foreground">Manage multiple warehouse facilities</p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button data-testid="create-warehouse-btn" className="bg-brand hover:bg-brand/90">
                <Plus size={18} className="mr-2" /> New Warehouse
              </Button>
            </DialogTrigger>
            <DialogContent className="border border-border rounded-none">
              <DialogHeader>
                <DialogTitle>Create Warehouse</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label className="overline">Name</Label>
                  <Input value={newWh.name} onChange={(e) => setNewWh({...newWh, name: e.target.value})} required className="mt-1" />
                </div>
                <div>
                  <Label className="overline">Code (uppercase)</Label>
                  <Input value={newWh.code} onChange={(e) => setNewWh({...newWh, code: e.target.value.toUpperCase()})} required maxLength={10} className="mt-1 font-mono" placeholder="MAIN, NYC01" />
                </div>
                <div>
                  <Label className="overline">Address</Label>
                  <Input value={newWh.address} onChange={(e) => setNewWh({...newWh, address: e.target.value})} className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="overline">Contact Name</Label>
                    <Input value={newWh.contact_name} onChange={(e) => setNewWh({...newWh, contact_name: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label className="overline">Contact Email</Label>
                    <Input type="email" value={newWh.contact_email} onChange={(e) => setNewWh({...newWh, contact_email: e.target.value})} className="mt-1" />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-brand hover:bg-brand/90">Create Warehouse</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            [1,2,3].map(i => <div key={i} className="h-40 bg-slate-200 animate-pulse"></div>)
          ) : warehouses.length === 0 ? (
            <div className="col-span-full text-center py-12 text-muted-foreground">
              <Buildings size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No warehouses yet</p>
            </div>
          ) : warehouses.map(wh => (
            <div key={wh.id} className={`border p-5 ${currentWarehouseId === wh.id ? 'bg-brand/5 border-brand' : 'bg-card border-border rounded-lg'}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-mono font-bold text-lg">{wh.code}</p>
                    {wh.is_default && <Star size={16} weight="fill" className="text-amber-500" />}
                    {currentWarehouseId === wh.id && <CheckCircle size={16} weight="fill" className="text-brand" />}
                  </div>
                  <p className="text-sm font-medium">{wh.name}</p>
                </div>
                <button
                  onClick={() => handleToggleActive(wh)}
                  className={`px-2 py-1 text-xs font-bold ${wh.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                >
                  {wh.is_active ? 'ACTIVE' : 'INACTIVE'}
                </button>
              </div>
              {wh.address && <p className="text-sm text-muted-foreground mb-3">{wh.address}</p>}
              {capacityByWh[wh.id] && (
                <div
                  className={`text-[11px] font-mono px-2 py-1 mb-2 border ${
                    capacityByWh[wh.id].warning
                      ? 'bg-rose-50 border-rose-300 text-rose-800'
                      : (capacityByWh[wh.id].projected_volume_pct ?? 0) >= 85
                        ? 'bg-amber-50 border-amber-300 text-amber-800'
                        : 'bg-emerald-50 border-emerald-300 text-emerald-800'
                  }`}
                  data-testid={`wh-capacity-badge-${wh.id}`}
                  title={capacityByWh[wh.id].warning?.message || 'Projected fill in the next 30 days'}
                >
                  {capacityByWh[wh.id].warning && <Warning size={11} weight="fill" className="inline mr-1" />}
                  Projected · vol {capacityByWh[wh.id].projected_volume_pct == null ? '—' : `${capacityByWh[wh.id].projected_volume_pct}%`} · pallets {capacityByWh[wh.id].projected_pallet_pct == null ? '—' : `${capacityByWh[wh.id].projected_pallet_pct}%`}
                </div>
              )}
              <div className="flex gap-2 mt-3 flex-wrap">
                {!wh.is_default && (
                  <Button size="sm" variant="outline" onClick={() => handleSetDefault(wh.id)}>
                    <Star size={14} className="mr-1" /> Make Default
                  </Button>
                )}
                {currentWarehouseId !== wh.id && (
                  <Button size="sm" onClick={() => switchWarehouse(wh.id)} className="bg-brand hover:bg-brand/90">
                    Switch To
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setRateCardFor(wh)} data-testid={`rate-card-btn-${wh.id}`} title="Edit transfer rate card">
                  <Coins size={14} className="mr-1" /> Rate card
                </Button>
              </div>
            </div>
          ))}
        </div>

        <WarehouseRateCardDialog
          open={!!rateCardFor}
          warehouse={rateCardFor}
          onClose={() => setRateCardFor(null)}
        />
      </div>
    </Layout>
  );
}
